import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    Box,
    Flex,
    FormLabel,
    Input,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    useTheme,
} from '@chakra-ui/react'
import { Select, SingleValue } from 'chakra-react-select'
import debounce from 'debounce'
import { gql } from 'urql'
import { AllRemunerationTimePeriods, RemunerationTimePeriod } from 'entities/job'
import { isServerSide } from 'lib/env'
import { formatNumber, numberOfDigits, roundAmount, tokenizeAmount } from 'lib/number'
import { toOptions } from 'lib/object'
import useQuery from 'hooks/useQuery'
import {
    currencySelectChakraStyles,
    getAvailableRemunerationRangeOptions,
    getRemunerationRangeOption,
    RemunerationRangeOption,
} from 'containers/jobs-board/components/CompensationFilter/assets'
import { FilterInput } from 'containers/jobs-board/components/FilterInput'
import { RemunerationRange } from 'containers/jobs-board/components/options'
import { useJobsSearchContext } from 'containers/jobs-board/context/JobsSearchContext'
import { Params } from 'containers/jobs-board/hooks/useParams'
import { ToggleSwitcher } from 'containers/upsert-job/components/ToggleSwitcher'

// NB: The same as for the legacy app (`wh.common.data/no-currency-range-max`).
const NO_CURRENCY_RANGE_MAX = {
    Yearly: 300000,
    Daily: 1000,
} as const

const QUERY_REMUNERATION_RANGES = gql<{
    remuneration_ranges: RemunerationRange[]
}>`
    query remuneration_ranges {
        remuneration_ranges {
            currency
            timePeriod
            min
            max
        }
    }
`

const getTimePeriod = (filters: Params) => {
    return filters.remunerationTimePeriod ?? AllRemunerationTimePeriods.Yearly
}

const getPreStep = (ratio: number, currencyRangeMax: number) => {
    // NB: These steps approximate a nice rounding curve.
    if (ratio < 0.05) {
        return roundAmount(currencyRangeMax / 1000)
    } else if (ratio < 0.1) {
        return roundAmount(currencyRangeMax / 500)
    } else if (ratio < 0.2) {
        return roundAmount(currencyRangeMax / 200)
    } else if (ratio < 0.4) {
        return roundAmount(currencyRangeMax / 100)
    } else if (ratio < 0.8) {
        return roundAmount(currencyRangeMax / 50)
    } else {
        return roundAmount(currencyRangeMax / 20)
    }
}

const getRemunerationScale = (currencyRangeMax: number): number[] => {
    const steps = tokenizeAmount(numberOfDigits(currencyRangeMax) - 1)

    let scale = [0]
    let val = 0
    do {
        const preStep = getPreStep(val / currencyRangeMax, currencyRangeMax)
        const step = steps.find(t => t >= preStep)!!

        // calculate a new val while rounding up after a step
        const last = scale[scale.length - 1]
        val = Math.round((last + step) / step) * step

        // keep adding vals as long as we're within the range
        if (val > currencyRangeMax) {
            break
        }
        scale = [...scale, val]
    } while (true)

    // add the max value, if it isn't there yet
    const last = scale[scale.length - 1]
    if (last < currencyRangeMax) {
        scale = [...scale, currencyRangeMax]
    }

    return scale
}

const getInputStep = (remunerationValue: number, currencyRangeMax: number) => {
    const steps = tokenizeAmount(numberOfDigits(currencyRangeMax) - 1)
    const preStep = getPreStep(remunerationValue / currencyRangeMax, currencyRangeMax)
    return steps.find(t => t >= preStep)
}

const CompensationSlider = ({
    currencyRangeMax,
    remunerationScale,
    remunerationValue,
    setRemunerationValue,
}: {
    currencyRangeMax: number
    remunerationScale: number[]
    remunerationValue: number
    setRemunerationValue: Dispatch<SetStateAction<number>>
}) => {
    const { updateFilters } = useJobsSearchContext()

    const selectedIdx = remunerationScale.findIndex(t => t >= remunerationValue)!!

    const theme = useTheme()
    const sliderTrackGradient = theme.gradients.slider_track

    const onCompensationRangeChange = (idx: number) => {
        updateFilters({
            remunerationMin: remunerationScale[idx],
        })
    }

    return (
        <Slider
            key={currencyRangeMax}
            colorScheme="secondary"
            aria-label="slider-ex-1"
            value={selectedIdx}
            min={0}
            max={remunerationScale.length - 1}
            onChange={idx => {
                setRemunerationValue(remunerationScale[idx])
            }}
            onChangeEnd={onCompensationRangeChange}
            focusThumbOnChange={false}
        >
            <SliderTrack
                h="8px"
                rounded="6px"
                backgroundImage={sliderTrackGradient}
                backgroundColor="gray.background"
            >
                <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb boxSize="36px" borderWidth="1.5px" borderColor="black" />
        </Slider>
    )
}

const CompensationInput = ({
    currencyRangeMax,
    remunerationValue,
    setRemunerationValue,
}: {
    currencyRangeMax: number
    remunerationValue: number
    setRemunerationValue: Dispatch<SetStateAction<number>>
}) => {
    const { updateFilters } = useJobsSearchContext()

    const [showInput, setShowInput] = useState(false)

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value)
        if (value >= 0 && value <= currencyRangeMax) {
            setRemunerationValue(value)
            handleSearchDebounce(e)
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleSearchDebounce = useCallback(
        debounce((e: ChangeEvent<HTMLInputElement>) => {
            updateFilters({
                remunerationMin: Number(e.target.value),
            })
        }, 500),
        [],
    )

    const inputValue =
        remunerationValue === 0
            ? ''
            : showInput
            ? remunerationValue
            : formatNumber(remunerationValue)

    return (
        <Box w="full">
            <FormLabel
                mb="2px"
                variant="bodyTextSmall"
                color="text.secondary"
                fontSize="sm"
            >
                From
            </FormLabel>
            <Input
                type={showInput ? 'number' : 'text'}
                placeholder="0"
                onChange={handleInputChange}
                value={inputValue}
                min={0}
                max={currencyRangeMax}
                step={getInputStep(remunerationValue, currencyRangeMax)}
                onBlur={() => {
                    setShowInput(false)
                }}
                onFocus={() => {
                    setShowInput(true)
                }}
            />
        </Box>
    )
}

const CurrencySelect = ({
    remunerationValue,
    setRemunerationValue,
    remunerationRanges,
    selectedRemunerationRange,
    setSelectedRemunerationRange,
}: {
    remunerationValue: number
    setRemunerationValue: Dispatch<SetStateAction<number>>
    remunerationRanges: RemunerationRange[]
    selectedRemunerationRange: RemunerationRangeOption | undefined
    setSelectedRemunerationRange: Dispatch<
        SetStateAction<RemunerationRangeOption | undefined>
    >
}) => {
    const { filters, updateFilters } = useJobsSearchContext()

    const onCurrencyChange = (currencyValue: SingleValue<RemunerationRangeOption>) => {
        const newValue = getRemunerationRangeOption(remunerationRanges, {
            currency: currencyValue?.value!!,
            timePeriod: getTimePeriod(filters),
        })
        setSelectedRemunerationRange(newValue)

        const newCurrencyRangeMax = newValue?.max ?? 0
        if (remunerationValue > newCurrencyRangeMax) {
            setRemunerationValue(newCurrencyRangeMax)
            updateFilters({
                remunerationMin: newCurrencyRangeMax,
            })
        }

        updateFilters({
            remunerationCurrency: currencyValue?.value,
        })
    }

    return (
        <Box w="full">
            <FormLabel
                mb="2px"
                variant="bodyTextSmall"
                color="text.secondary"
                fontSize="sm"
            >
                Currency
            </FormLabel>
            <Select<RemunerationRangeOption>
                placeholder="All currencies"
                options={getAvailableRemunerationRangeOptions(remunerationRanges)}
                value={selectedRemunerationRange}
                chakraStyles={currencySelectChakraStyles}
                colorScheme="red"
                selectedOptionColor="secondary"
                onChange={onCurrencyChange}
                isClearable={true}
            />
        </Box>
    )
}

const TimePeriodSelector = ({
    setRemunerationValue,
    remunerationRanges,
    selectedRemunerationRange,
    setSelectedRemunerationRange,
}: {
    setRemunerationValue: Dispatch<SetStateAction<number>>
    remunerationRanges: RemunerationRange[]
    selectedRemunerationRange: RemunerationRangeOption | undefined
    setSelectedRemunerationRange: Dispatch<
        SetStateAction<RemunerationRangeOption | undefined>
    >
}) => {
    const { filters, updateFilters } = useJobsSearchContext()

    // Time period handler (Daily and Yearly)
    const onTimePeriodChange = (value: RemunerationTimePeriod) => {
        setSelectedRemunerationRange(
            getRemunerationRangeOption(remunerationRanges, {
                currency: selectedRemunerationRange?.value!!,
                timePeriod: value,
            }),
        )
        setRemunerationValue(0)

        updateFilters({
            remunerationTimePeriod: value,
            remunerationMin: undefined, // KISS, no auto-recalculation
        })
    }

    return (
        <ToggleSwitcher
            options={toOptions(AllRemunerationTimePeriods)}
            field={{
                onChange: onTimePeriodChange,
                size: 'sm',
                value: getTimePeriod(filters),
            }}
        />
    )
}

export const CompensationFilter = () => {
    const { filters } = useJobsSearchContext()

    const [{ data }] = useQuery({
        query: QUERY_REMUNERATION_RANGES,
        pause: isServerSide,
    })

    // Remuneration Ranges
    const remunerationRanges = data?.remuneration_ranges ?? []

    const [selectedRemunerationRange, setSelectedRemunerationRange] = useState<
        RemunerationRangeOption | undefined
    >()

    useEffect(() => {
        const defaultSelectedRange = filters.remunerationCurrency
            ? getRemunerationRangeOption(remunerationRanges, {
                  currency: filters.remunerationCurrency,
                  timePeriod: getTimePeriod(filters),
              })
            : undefined
        setSelectedRemunerationRange(defaultSelectedRange)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remunerationRanges])

    const currencyRangeMax =
        selectedRemunerationRange?.max ?? NO_CURRENCY_RANGE_MAX[getTimePeriod(filters)]

    const remunerationScale = useMemo(() => {
        return getRemunerationScale(currencyRangeMax)
    }, [currencyRangeMax])

    // Remuneration Value
    const [remunerationValue, setRemunerationValue] = useState(
        filters.remunerationMin ?? 0,
    )
    useEffect(() => {
        if (remunerationValue !== filters.remunerationMin) {
            setRemunerationValue(filters.remunerationMin ?? 0)
        }
    }, [filters.remunerationMin])

    return (
        <FilterInput label="Compensation" minWidth={{ base: '320px', md: '390px' }}>
            <Text color="text.secondary" variant="bodyTextSmall">
                Desired compensation
            </Text>

            <Flex mt="16px">
                <CompensationSlider
                    currencyRangeMax={currencyRangeMax}
                    remunerationScale={remunerationScale}
                    remunerationValue={remunerationValue}
                    setRemunerationValue={setRemunerationValue}
                />
            </Flex>

            <Flex mt="12px" gap="12px" justifyContent="space-between">
                <CompensationInput
                    currencyRangeMax={currencyRangeMax}
                    remunerationValue={remunerationValue}
                    setRemunerationValue={setRemunerationValue}
                />

                <CurrencySelect
                    remunerationValue={remunerationValue}
                    setRemunerationValue={setRemunerationValue}
                    remunerationRanges={remunerationRanges}
                    selectedRemunerationRange={selectedRemunerationRange}
                    setSelectedRemunerationRange={setSelectedRemunerationRange}
                />
            </Flex>

            <Box mt="20px">
                <TimePeriodSelector
                    setRemunerationValue={setRemunerationValue}
                    remunerationRanges={remunerationRanges}
                    selectedRemunerationRange={selectedRemunerationRange}
                    setSelectedRemunerationRange={setSelectedRemunerationRange}
                />
            </Box>
        </FilterInput>
    )
}
